import React, { useState } from 'react';
import {
    Box,
    Typography,
    Checkbox,
    FormControlLabel,
    Button,
    Stack,
    Dialog,
    DialogContent,
    DialogActions,
    IconButton,
} from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import CloseIcon from '@mui/icons-material/Close';

const levels = ['beginner', 'intermediate', 'expert'];
const domains = ['cloud engineering', 'cybersecurity', 'devops', 'devsecops'];

function Sidebar({
    selectedLevels,
    setSelectedLevels,
    selectedDomains,
    setSelectedDomains,
    selectedType,
    setSelectedType,
}) {
    const [open, setOpen] = useState(false); // State to control dialog visibility

    const handleTypeChange = (type) => {
        setSelectedType((prev) =>
            prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type]
        );
    };

    const handleLevelChange = (level) => {
        setSelectedLevels((prev) =>
            prev.includes(level) ? prev.filter((l) => l !== level) : [...prev, level]
        );
    };

    const handleDomainChange = (domain) => {
        setSelectedDomains((prev) =>
            prev.includes(domain) ? prev.filter((d) => d !== domain) : [...prev, domain]
        );
    };

    const handleClearAll = () => {
        setSelectedLevels([]);
        setSelectedDomains([]);
        setSelectedType([]);
    };

    const toggleDialog = () => setOpen((prev) => !prev);

    return (
        <Box>
            {/* Desktop Sidebar */}
            <Box
                sx={{
                    display: { xs: 'none', sm: 'block' }, // Hidden on mobile, visible on larger screens
                    borderRight: '1px solid #ddd',
                    pr: 2,
                    height: '100%',
                }}
            >
                {/* Filters Heading with Clear All */}
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                    <Typography variant="subtitle1" sx={{ color: '#777', fontWeight: 'bold', fontSize: '0.9rem' }}>
                        Filters
                    </Typography>
                    <Button
                        onClick={handleClearAll}
                        sx={{
                            textTransform: 'none',
                            fontSize: '0.85rem',
                            color: '#555',
                            backgroundColor: 'transparent',
                            '&:hover': { backgroundColor: '#f0f0f0' },
                        }}
                    >
                        Clear All
                    </Button>
                </Stack>

                {/* Filter by Type */}
                <Typography
                    variant="subtitle2"
                    sx={{
                        mb: 1,
                        color: '#888',
                        fontWeight: 'bold',
                        fontSize: '0.8rem',
                    }}
                >
                    Type
                </Typography>
                <Stack spacing={1} sx={{ mb: 3 }}>
                    <FormControlLabel
                        control={
                            <Checkbox
                                value="course"
                                checked={selectedType.includes('course')}
                                onChange={() => handleTypeChange('course')}
                            />
                        }
                        label={
                            <Typography sx={{ fontSize: '0.8rem', color: '#555' }}>
                                Course
                            </Typography>
                        }
                    />
                    <FormControlLabel
                        control={
                            <Checkbox
                                value="lab"
                                checked={selectedType.includes('lab')}
                                onChange={() => handleTypeChange('lab')}
                            />
                        }
                        label={
                            <Typography sx={{ fontSize: '0.8rem', color: '#555' }}>
                                Lab
                            </Typography>
                        }
                    />
                </Stack>

                {/* Filter by Level */}
                <Typography
                    variant="subtitle2"
                    sx={{
                        mb: 1,
                        color: '#888',
                        fontWeight: 'bold',
                        fontSize: '0.8rem',
                    }}
                >
                    Level
                </Typography>
                <Stack spacing={1} sx={{ mb: 3 }}>
                    {levels.map((level) => (
                        <FormControlLabel
                            key={level}
                            control={
                                <Checkbox
                                    checked={selectedLevels.includes(level)}
                                    onChange={() => handleLevelChange(level)}
                                />
                            }
                            label={
                                <Typography
                                    sx={{
                                        fontSize: '0.8rem',
                                        color: '#555',
                                    }}
                                >
                                    {level[0].toLocaleUpperCase() + level.slice(1)}
                                </Typography>
                            }
                        />
                    ))}
                </Stack>

                {/* Filter by Domain */}
                <Typography
                    variant="subtitle2"
                    sx={{
                        mb: 1,
                        color: '#888',
                        fontWeight: 'bold',
                        fontSize: '0.8rem',
                    }}
                >
                    Domains
                </Typography>
                <Stack spacing={1}>
                    {domains.map((domain) => (
                        <FormControlLabel
                            key={domain}
                            control={
                                <Checkbox
                                    checked={selectedDomains.includes(domain)}
                                    onChange={() => handleDomainChange(domain)}
                                />
                            }
                            label={
                                <Typography
                                    sx={{
                                        fontSize: '0.8rem',
                                        color: '#555',
                                    }}
                                >
                                    {domain
                                        .split(' ')
                                        .map((word) => word[0].toUpperCase() + word.slice(1))
                                        .join(' ')}
                                </Typography>
                            }
                        />
                    ))}
                </Stack>
            </Box>

            {/* Mobile Filter Button */}
            <Box sx={{ display: { xs: 'block', sm: 'none' }, textAlign: 'center' }}>
                <Button
                    variant="outlined"
                    startIcon={<FilterListIcon />}
                    onClick={toggleDialog}
                    sx={{
                        textTransform: 'none',
                        fontSize: '0.875rem',
                        width: '100%',
                    }}
                >
                    Filters
                </Button>
            </Box>

            {/* Mobile Filter Dialog */}
            <Dialog
                open={open}
                onClose={toggleDialog}
                sx={{
                    '& .MuiDialog-paper': {
                        height: '70vh', // 2/3 of screen height
                        position: 'fixed',
                        bottom: 0,
                        margin: 0, // Removes any margin
                        padding: 0, // Ensures no internal padding that might add spacing
                        width: '100%',
                        borderTopLeftRadius: '16px',
                        borderTopRightRadius: '16px',
                    },
                }}
            >
                <DialogContent>
                    {/* Filters Header with Close Icon and Clear All */}
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                        <Box display="flex" alignItems="center">
                            <IconButton
                                onClick={toggleDialog}
                                sx={{
                                    mr: 1,
                                    color: '#555',
                                }}
                            >
                                <CloseIcon />
                            </IconButton>
                            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', fontSize: '1rem' }}>
                                Filters
                            </Typography>
                        </Box>
                        <Button
                            onClick={handleClearAll}
                            sx={{
                                textTransform: 'none',
                                fontSize: '0.85rem',
                                color: '#555',
                                backgroundColor: 'transparent',
                                '&:hover': { backgroundColor: '#f0f0f0' },
                            }}
                        >
                            Clear All
                        </Button>
                    </Stack>

                    {/* Filter by Type */}
                    <Typography
                        variant="subtitle2"
                        sx={{ mb: 1, color: '#888', fontWeight: 'bold', fontSize: '0.8rem' }}
                    >
                        Type
                    </Typography>
                    <Stack spacing={1} sx={{ mb: 3 }}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    value="course"
                                    checked={selectedType.includes('course')}
                                    onChange={() => handleTypeChange('course')}
                                />
                            }
                            label={
                                <Typography sx={{ fontSize: '0.8rem', color: '#555' }}>
                                    Course
                                </Typography>
                            }
                        />
                        <FormControlLabel
                            control={
                                <Checkbox
                                    value="lab"
                                    checked={selectedType.includes('lab')}
                                    onChange={() => handleTypeChange('lab')}
                                />
                            }
                            label={
                                <Typography sx={{ fontSize: '0.8rem', color: '#555' }}>
                                    Lab
                                </Typography>
                            }
                        />
                    </Stack>

                    {/* Filter by Level */}
                    <Typography
                        variant="subtitle2"
                        sx={{ mb: 1, color: '#888', fontWeight: 'bold', fontSize: '0.8rem' }}
                    >
                        Level
                    </Typography>
                    <Stack spacing={1} sx={{ mb: 3 }}>
                        {levels.map((level) => (
                            <FormControlLabel
                                key={level}
                                control={
                                    <Checkbox
                                        checked={selectedLevels.includes(level)}
                                        onChange={() => handleLevelChange(level)}
                                    />
                                }
                                label={
                                    <Typography
                                        sx={{
                                            fontSize: '0.8rem',
                                            color: '#555',
                                        }}
                                    >
                                        {level[0].toLocaleUpperCase() + level.slice(1)}
                                    </Typography>
                                }
                            />
                        ))}
                    </Stack>
                </DialogContent>

                {/* Sticky "Show Results" Button */}
                <DialogActions sx={{ px: 2, pb: 2 }}>
                    <Button
                        variant="contained"
                        fullWidth
                        onClick={toggleDialog}
                        sx={{ textTransform: 'none', fontSize: '1rem' }}
                    >
                        Show Results
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

export default Sidebar;
